import React from 'react';
// @ts-ignore
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  ChartBarIcon, 
  Cog6ToothIcon, 
  CalendarIcon,
  HeartIcon,
  UserGroupIcon,
  UserIcon,
  PlusCircleIcon,
  ChartPieIcon,
  StarIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
// @ts-ignore
import { useAppTheme } from '@/context/ThemeContext';
import { useState, useEffect } from 'react';

type NavItem = {
  name: string;
  path: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  parameter?: number;
  children?: NavItem[];
  permissions?: string[];
};

type CharitySidebarProps = {
  isOpen: boolean;
  isMinimized: boolean;
  onClose: () => void;
  theme?: string;
};

type NavItemProps = {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  isMinimized: boolean;
};

const charityNavigation: NavItem[] = [
  { name: 'Dashboard', path: '/charity/dashboard', icon: HomeIcon, parameter: 1 },
  { 
    name: 'Donations', 
    path: '/charity/donations', 
    icon: HeartIcon, 
    parameter: 2,
    children: [
      { name: 'All Donations', path: '/charity/donations', icon: HeartIcon },
      { name: 'Request Donations', path: '/charity/donations/request', icon: PlusCircleIcon }
    ]
  },
  { 
    name: 'Events', 
    path: '/charity/events', 
    icon: CalendarIcon, 
    parameter: 3,
    children: [
      { name: 'All Events', path: '/charity/events', icon: CalendarIcon },
      { name: 'Add Event', path: '/charity/events/create', icon: PlusCircleIcon }
    ]
  },
  { name: 'Volunteers', path: '/charity/volunteers', icon: UserGroupIcon, parameter: 4 },
  { 
    name: 'Statistics', 
    path: '/charity/statistics', 
    icon: ChartPieIcon, 
    parameter: 5, 
    children: [
      { name: 'Ratings & Reviews', path: '/charity/statistics/ratings', icon: StarIcon },
      { name: 'Social Impact', path: '/charity/statistics/social-impact', icon: HeartIcon },
      { name: 'Analytics', path: '/charity/statistics/analytics', icon: ChartBarIcon }
    ]
  },
  { 
    name: 'Settings', 
    path: '/settings', 
    icon: Cog6ToothIcon, 
    parameter: 6,
    children: [
      { name: 'Profile Settings', path: '/settings/charity-profile-settings', icon: UserIcon },
      { name: 'Charity Settings', path: '/charity/settings', icon: Cog6ToothIcon },
      { name: 'Staff Management', path: '/charity/users', icon: UserGroupIcon }
    ]
  },
  {
    name: 'Help Center',
    path: '/charity/help',
    icon: QuestionMarkCircleIcon,
    parameter: 7,
    children: [
      { name: 'Help Center', path: '/charity/help', icon: QuestionMarkCircleIcon },
      { name: 'FAQ', path: '/charity/help/faq', icon: QuestionMarkCircleIcon },
      { name: 'Report an Issue', path: '/charity/help/report-issue', icon: ExclamationTriangleIcon },
      { name: 'Contact Support', path: '/charity/help/contact-support', icon: ChatBubbleLeftRightIcon }
    ]
  },
];

const charityDevNavigation: NavItem[] = [
  { name: 'Dashboard', path: '/dev/dashboard', icon: HomeIcon, parameter: 1 },
  { name: 'Donations', path: '/dev/donations', icon: HeartIcon, parameter: 2 },
  { name: 'Events', path: '/dev/events', icon: CalendarIcon, parameter: 3 },
  { name: 'Volunteers', path: '/dev/volunteers', icon: UserGroupIcon, parameter: 4 },
  { name: 'Settings', path: '/dev/settings', icon: Cog6ToothIcon, parameter: 5 },
];

// NavItem component with dropdown support
const NavItemComponent = ({ item, isActive, onClick, isMinimized }: NavItemProps) => {
  const { themeConfig } = useAppTheme();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Determine if this item or any of its children is active
  const isItemActive = isActive || (item.children?.some(child => 
    location.pathname === child.path || 
    location.pathname.startsWith(`${child.path}/`)
  ) ?? false);

  // For items with children, toggle dropdown
  const handleClick = () => {
    if (item.children) {
      setIsOpen(!isOpen);
    } else {
      onClick();
    }
  };

  return (
    <div>
      {/* Parent Item */}
      <div className="relative">
        {item.children ? (
          // Item with dropdown
          <div 
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`flex items-center px-3 py-2 text-sm rounded-md cursor-pointer ${
              isItemActive ? 'bg-blue-50' : ''
            } transition-colors duration-200`}
            style={{ 
              backgroundColor: isItemActive 
                ? themeConfig.accent
                : isHovered 
                  ? themeConfig.surface 
                  : 'transparent'
            }}
          >
            <item.icon 
              className="h-5 w-5 flex-shrink-0" 
              style={{ 
                color: isItemActive 
                  ? '#0c4a8a' // Darker blue for active icon
                  : themeConfig.text
              }}
              aria-hidden="true" 
            />
            {!isMinimized && (
              <>
                <span 
                  className="ml-3 text-sm font-medium flex-grow"
                  style={{ 
                    color: isItemActive 
                      ? '#0c4a8a' // Darker blue for active text
                      : themeConfig.text
                  }}
                >
                  {item.name}
                </span>
                <ChevronDownIcon 
                  className={`h-4 w-4 transition-transform transform ${isOpen ? 'rotate-180' : ''}`}
                  style={{ color: themeConfig.text }}
                />
              </>
            )}
          </div>
        ) : (
          // Regular item
          <Link
            to={item.path}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`flex items-center px-3 py-2 text-sm rounded-md ${
              isItemActive ? 'bg-blue-50' : ''
            } transition-colors duration-200`}
            style={{ 
              backgroundColor: isItemActive 
                ? themeConfig.accent
                : isHovered 
                  ? themeConfig.surface 
                  : 'transparent'
            }}
          >
            <item.icon 
              className="h-5 w-5 flex-shrink-0" 
              style={{ 
                color: isItemActive 
                  ? '#0c4a8a' // Darker blue for active icon
                  : themeConfig.text
              }}
              aria-hidden="true" 
            />
            {!isMinimized && (
              <span 
                className="ml-3 text-sm font-medium"
                style={{ 
                  color: isItemActive 
                    ? '#0c4a8a' // Darker blue for active text
                    : themeConfig.text
                }}
              >
                {item.name}
              </span>
            )}
          </Link>
        )}
      </div>

      {/* Dropdown Children */}
      {item.children && isOpen && !isMinimized && (
        <div className="mt-1 ml-6 space-y-0.5">
          {item.children.map(child => (
            <Link
              key={child.name}
              to={child.path}
              onClick={() => onClick()}
              className="flex items-center px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors duration-200"
              style={{ 
                color: themeConfig.text,
                backgroundColor: location.pathname === child.path ? themeConfig.accent : 'transparent',
              }}
            >
              {child.icon && (
                <child.icon 
                  className="h-4 w-4 mr-2" 
                  style={{ 
                    color: location.pathname === child.path ? '#0c4a8a' : themeConfig.text
                  }} 
                />
              )}
              <span 
                className="text-sm"
                style={{ 
                  color: location.pathname === child.path ? '#0c4a8a' : themeConfig.text
                }}
              >
                {child.name}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CharitySidebar({ isOpen, isMinimized, onClose }: CharitySidebarProps) {
  const location = useLocation();
  const { themeConfig } = useAppTheme();
  const [activeParameter, setActiveParameter] = useState<number>(1);
  
  // Determine which navigation to show based on route
  const isDevRoute = location.pathname.startsWith('/dev');
  
  const getNavigation = () => {
    if (isDevRoute) {
      return charityDevNavigation;
    }
    return charityNavigation;
  };
  
  const navigation = getNavigation();

  // Set active parameter based on location
  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = navigation.find(item => {
      if (item.path === '/' && currentPath === '/') return true;
      if (item.path !== '/' && currentPath.startsWith(item.path)) return true;
      return false;
    });
    
    if (activeItem?.parameter) {
      setActiveParameter(activeItem.parameter);
    }
  }, [location.pathname, navigation]);

  // Handle navigation item click
  const handleNavItemClick = (item: NavItem) => {
    setActiveParameter(item.parameter || 1);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/30 lg:hidden" 
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar container */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isMinimized ? 'lg:w-16' : 'lg:w-[240px]'}
          w-[240px] p-2`}
      >
        <div className="flex flex-col h-full rounded-xl overflow-hidden shadow-md border border-opacity-30"
          style={{
            backgroundColor: themeConfig.background,
            borderColor: '#4A90E2', // Fixed blue color for sidebar border
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
          }}
        >
          {/* Logo section */}
          <div className="flex items-center justify-center h-16 px-3">
            <img 
              src="/favicon.png" 
              alt="Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          {/* Divider */}
          <div className="h-[1px] w-full" style={{ backgroundColor: '#4A90E290' }}></div>

          {/* Navigation section */}
          <div className="flex-1 overflow-y-auto py-3">
            {/* Navigation items */}
            <div className="space-y-0.5">
              {navigation.map((item) => (
                <NavItemComponent
                  key={item.name}
                  item={item}
                  isActive={activeParameter === item.parameter}
                  onClick={() => handleNavItemClick(item)}
                  isMinimized={isMinimized}
                />
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
